/**
 * Administration → Mail. One SMTP account for the selected connection.
 * Password is never loaded back; an empty save keeps the stored secret.
 */
import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'

import { useConnection } from '@/app/contexts/ConnectionContext'
import {
  connectionMailApi,
  type ConnectionMailSave,
  type ConnectionMailSettings,
} from '@/app/services/connectionMailApi'

const PASSWORD_KEPT = 'A password is saved. Leave this blank to keep it.'
const SELECT_CONNECTION = 'Select a connection to configure mail.'

interface MailForm {
  host: string
  port: number
  username: string
  from_addr: string
  use_tls: boolean
  password: string
}

function formFrom(settings: ConnectionMailSettings): MailForm {
  return {
    host: settings.host,
    port: settings.port,
    username: settings.username,
    from_addr: settings.from_addr,
    use_tls: settings.use_tls,
    password: '',
  }
}

function saveBody(form: MailForm): ConnectionMailSave {
  return {
    host: form.host,
    port: form.port,
    username: form.username,
    from_addr: form.from_addr,
    use_tls: form.use_tls,
    password: form.password,
  }
}

/** Server sentences arrive as axios `response.data.detail`, or as `{ detail }`
 * after ApiService.handleError unwraps the response. */
export function mailErrorText(err: unknown): string {
  if (typeof err !== 'object' || err === null) return 'Request failed'
  const axiosDetail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
  if (typeof axiosDetail === 'string' && axiosDetail) return axiosDetail
  const plain = (err as { detail?: unknown }).detail
  if (typeof plain === 'string' && plain) return plain
  return 'Request failed'
}

export const ConnectionMailPage: React.FC = () => {
  const { selectedConnection } = useConnection()
  const connectionId = selectedConnection?.id ?? null
  const [server, setServer] = useState<ConnectionMailSettings | null>(null)
  const [form, setForm] = useState<MailForm | null>(null)
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [snack, setSnack] = useState<string | null>(null)

  useEffect(() => {
    if (!connectionId) return undefined
    let cancelled = false
    setLoading(true)
    setError(null)
    setServer(null)
    setForm(null)
    connectionMailApi.get(connectionId)
      .then((payload) => {
        if (cancelled) return
        setServer(payload)
        setForm(formFrom(payload))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(mailErrorText(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [connectionId])

  const applyPayload = (payload: ConnectionMailSettings) => {
    setServer(payload)
    setForm(formFrom(payload))
  }

  const handleSave = async () => {
    if (!connectionId || !form) return
    setError(null)
    try {
      applyPayload(await connectionMailApi.save(connectionId, saveBody(form)))
    } catch (err) {
      setError(mailErrorText(err))
    }
  }

  const handleRemove = async () => {
    if (!connectionId) return
    setError(null)
    try {
      applyPayload(await connectionMailApi.remove(connectionId))
    } catch (err) {
      setError(mailErrorText(err))
    }
  }

  const handleTest = async () => {
    if (!connectionId || !server?.configured) return
    const address = to.trim()
    if (!address) {
      setError('Enter an address.')
      return
    }
    setError(null)
    try {
      await connectionMailApi.sendTest(connectionId, { to: address })
      setSnack('Sent')
    } catch (err) {
      setError(mailErrorText(err))
    }
  }

  if (!connectionId) {
    return (
      <Box sx={{ p: 3, maxWidth: 640 }}>
        <Typography variant="h5" gutterBottom>Mail</Typography>
        <Alert severity="info">{SELECT_CONNECTION}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h5" gutterBottom>Mail</Typography>
      {loading && !form && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      {form && server && (
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Host"
            value={form.host}
            onChange={(e) => setForm({ ...form, host: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            label="Port"
            type="number"
            value={form.port}
            onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
            size="small"
            fullWidth
          />
          <TextField
            label="Username"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            helperText={server.password_set ? PASSWORD_KEPT : undefined}
            size="small"
            fullWidth
            autoComplete="new-password"
          />
          <TextField
            label="From"
            value={form.from_addr}
            onChange={(e) => setForm({ ...form, from_addr: e.target.value })}
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={form.use_tls}
                onChange={(e) => setForm({ ...form, use_tls: e.target.checked })}
              />
            )}
            label="Use TLS"
          />
          <Box display="flex" gap={1}>
            <Button variant="contained" onClick={handleSave}>Save</Button>
            {server.configured && (
              <Button variant="outlined" color="error" onClick={handleRemove}>Remove</Button>
            )}
          </Box>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              label="To"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              size="small"
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={!server.configured}
              sx={{ flexShrink: 0, mt: 0.5 }}
            >
              Send test
            </Button>
          </Box>
        </Box>
      )}
      <Snackbar
        open={snack !== null}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />
    </Box>
  )
}
